import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, Menu, MenuItem } from '@mui/material';
import { useId, useState } from 'react';

import type { ReportExportFormat } from '../../api/report-transactions.api.js';

/** The financial reports' Export button (DEV-148): Excel or CSV, downloaded under one file name. */

const FORMATS: Array<{ format: ReportExportFormat; label: string }> = [
  { format: 'xlsx', label: 'Excel (.xlsx)' },
  { format: 'csv', label: 'CSV' },
];

export interface ReportExport {
  exporting: boolean;
  error: string;
  clearError: () => void;
  run: (format: ReportExportFormat) => Promise<void>;
}

/** Fetches the file and saves it as `{baseName}.{format}`. */
export function useReportExport(fetchFile: (format: ReportExportFormat) => Promise<Blob>, baseName: string): ReportExport {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const run = async (format: ReportExportFormat): Promise<void> => {
    setExporting(true); setError('');
    try {
      const blob = await fetchFile(format);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `${baseName}.${format}`;
      document.body.append(anchor); anchor.click(); anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure: unknown) { setError(failure instanceof Error ? failure.message : 'Export failed. Please retry.'); }
    finally { setExporting(false); }
  };
  return { exporting, error, clearError: () => setError(''), run };
}

interface MenuProps { exporter: ReportExport; disabled: boolean }

export function ReportExportMenu({ exporter, disabled }: MenuProps): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const pick = (format: ReportExportFormat): void => { setAnchor(null); void exporter.run(format); };
  return <>
    <Button variant="outlined" startIcon={<DownloadIcon />} endIcon={<ArrowDropDownIcon />} disabled={disabled || exporter.exporting}
      aria-haspopup="menu" aria-expanded={anchor !== null} aria-controls={anchor ? menuId : undefined}
      onClick={(event) => setAnchor(event.currentTarget)}>{exporter.exporting ? 'Exporting…' : 'Export'}</Button>
    <Menu id={menuId} anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      {FORMATS.map((f) => <MenuItem key={f.format} onClick={() => pick(f.format)}>{f.label}</MenuItem>)}
    </Menu>
  </>;
}
